import { useAlert } from '@positivote/design-system/hooks'

import {
  ApplicationException,
  ForbiddenException,
  NotFoundException,
  UnauthorizedException,
  UnprocessableEntityException,
  ValidationException
} from '@/common/exceptions'

interface HandleErrorProps {
  error: ApplicationException
  setValidations?: (validations: Record<string, string>) => void
  ignoreSignOut?: boolean
}

interface ErrorHandlerHook {
  handleError: (data: HandleErrorProps) => void
}

export function useErrorHandler(): ErrorHandlerHook {
  const { addAlertMessage } = useAlert()

  function handleError({ error, setValidations, ignoreSignOut }: HandleErrorProps): void {
    switch (error.constructor) {
      case ValidationException:
        setValidations?.(
          (error as ValidationException).validations.reduce(
            (accumulatedValidations, currentValidation) => ({
              ...accumulatedValidations,
              [currentValidation.field]: currentValidation.message
            }),
            {}
          ) as Record<string, string>
        )
        break
      case UnauthorizedException:
        addAlertMessage({
          alertKey: 'unauthorizedException',
          subTitle: error.message,
          severity: 'warning'
        })
        break
      case NotFoundException:
        addAlertMessage({
          alertKey: 'notFoundException',
          subTitle: error.message,
          severity: 'warning'
        })
        break
      case UnprocessableEntityException:
        addAlertMessage({
          alertKey: 'unprocessableEntityException',
          subTitle: error.message,
          severity: 'warning'
        })
        break
      case ForbiddenException:
        addAlertMessage({
          alertKey: 'forbiddenException',
          subTitle: error.message,
          severity: 'warning'
        })
        break
      default: {
        const parsedError = new ApplicationException({ ...error, originalError: error })
        addAlertMessage({
          alertKey: 'applicationException',
          subTitle: parsedError.message,
          severity: 'warning'
        })
        break
      }
    }
  }

  return { handleError }
}
